/**
 * Triple M Trading Cards - Theme JavaScript
 */

(function() {
  'use strict';

  /* ==========================================================================
     Mega Menu
     ========================================================================== */

  class MegaMenu {
    constructor(element) {
      this.menu = element;
      this.trigger = element.querySelector('[data-mega-menu-trigger]');
      this.panel = element.querySelector('[data-mega-menu-panel]');
      this.openedByMobileTap = false;

      if (!this.trigger || !this.panel) return;

      this.init();
    }

    init() {
      this.addCollectionLinkIndicators();

      this.trigger.addEventListener('click', (e) => {
        if (!this.isMobile()) return;
        const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
        const triggerBounds = this.trigger.getBoundingClientRect();
        const clickedCollectionLinkIndicator = e.target instanceof Element &&
          Boolean(e.target.closest('.mega-menu__collection-link-indicator'));
        const clickedToggleIcon = !clickedCollectionLinkIndicator && (
          (e.target instanceof Element && Boolean(e.target.closest('.mega-menu__trigger-icon'))) ||
          e.clientX >= triggerBounds.right - 44
        );
        if (isOpen && this.openedByMobileTap && this.trigger.tagName === 'A' && this.trigger.href && !clickedToggleIcon) {
          return;
        }
        e.preventDefault();
        if (clickedToggleIcon) e.stopPropagation();
        document.querySelectorAll('[data-mega-menu-trigger][aria-expanded="true"]').forEach(trigger => {
          if (trigger !== this.trigger) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
        if (isOpen) {
          this.close();
        } else {
          this.open({ mobileTap: true });
        }
      });

      // Handle keyboard navigation
      this.trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // Allow link to work on Enter, but toggle menu on Space
          if (e.key === ' ') {
            e.preventDefault();
            this.toggle();
          }
        }
        if (e.key === 'Escape') {
          this.close();
          this.trigger.focus();
        }
      });

      // Handle focus within panel
      this.panel.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close();
          this.trigger.focus();
        }
      });

      document.addEventListener('click', (e) => {
        if (!window.matchMedia('(max-width: 900px)').matches) return;
        if (!this.menu.contains(e.target)) {
          this.close();
        }
      });
    }

    addCollectionLinkIndicators() {
      const collectionLinks = [
        this.trigger,
        ...this.panel.querySelectorAll('.mega-menu__item[data-has-submenu] > .mega-menu__link'),
      ];
      collectionLinks.forEach((link) => {
        if (link.tagName !== 'A' || !link.hasAttribute('href') || link.querySelector('.mega-menu__collection-link-indicator')) return;
        const indicator = document.createElement('span');
        indicator.className = 'mega-menu__collection-link-indicator';
        indicator.setAttribute('aria-hidden', 'true');
        const referenceNode = link.querySelector('.mega-menu__availability-note, .mega-menu__trigger-icon, .mega-menu__link-icon');
        link.insertBefore(indicator, referenceNode);
      });
    }

    open(options = {}) {
      this.openedByMobileTap = Boolean(options.mobileTap);
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.openedByMobileTap = false;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    toggle() {
      const isOpen = this.trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    isMobile() {
      return window.matchMedia('(max-width: 900px)').matches;
    }
  }

  // Preserve an open branch while the mouse crosses other rows toward its panel.
  class MenuHoverIntent {
    constructor(root, topLevel = false) {
      this.root = root;
      this.topLevel = topLevel;
      this.active = null;
      this.previous = null;
      this.pending = null;
      this.timer = null;
      root.dataset.hoverManaged = '';
      root.addEventListener('pointermove', event => this.move(event));
      root.addEventListener('pointerleave', event => {
        if (!this.enabled(event)) return;
        this.cancel();
        this.timer = window.setTimeout(() => this.activate(null), 120);
      });
      root.addEventListener('pointerdown', event => {
        if (this.enabled(event)) this.activate(this.itemAt(event.target));
      });
      root.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.activate(null);
      });
      root.addEventListener('menuhoverreset', () => {
        this.activate(null);
        this.previous = null;
      });
      window.matchMedia('(max-width: 900px)').addEventListener('change', () => {
        this.activate(null);
        this.previous = null;
      });
    }

    enabled(event) {
      return event.pointerType === 'mouse' && window.matchMedia('(min-width: 901px)').matches;
    }

    itemAt(target) {
      let item = target;
      while (item && item.parentElement !== this.root) item = item.parentElement;
      return item?.matches('.mega-menu, .mega-menu__item') ? item : null;
    }

    panelFor(item) {
      return item?.querySelector(':scope > .mega-menu__panel, :scope > .mega-menu__submenu');
    }

    cancel() {
      window.clearTimeout(this.timer);
      this.timer = null;
      this.pending = null;
    }

    activate(item) {
      this.cancel();
      if (this.active) this.setOpen(this.active, false);
      this.active = item;
      if (item) this.setOpen(item, true);
    }

    setOpen(item, open) {
      item.classList.toggle('is-hover-active', open);
      if (this.topLevel) {
        item.querySelector('[data-mega-menu-trigger]')?.setAttribute('aria-expanded', String(open));
      }
      if (!open) {
        item.querySelectorAll('[data-hover-managed]').forEach(root => root.dispatchEvent(new Event('menuhoverreset')));
      }
    }

    towardPanel(point, origin) {
      const panel = this.panelFor(this.active);
      if (!panel || !origin || performance.now() - origin.time > 400) return false;
      const bounds = panel.getBoundingClientRect();
      const padding = 10;
      const first = this.topLevel ? { x: bounds.left - padding, y: bounds.top } : { x: bounds.left, y: bounds.top - padding };
      const second = this.topLevel ? { x: bounds.right + padding, y: bounds.top } : { x: bounds.left, y: bounds.bottom + padding };
      const distance = this.topLevel ? Math.abs(bounds.top - point.y) : Math.abs(bounds.left - point.x);
      const oldDistance = this.topLevel ? Math.abs(bounds.top - origin.y) : Math.abs(bounds.left - origin.x);
      if (distance >= oldDistance) return false;
      const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
      const sides = [cross(origin, first, point), cross(first, second, point), cross(second, origin, point)];
      return sides.every(side => side >= 0) || sides.every(side => side <= 0);
    }

    move(event) {
      if (!this.enabled(event)) return;
      const point = { x: event.clientX, y: event.clientY, time: performance.now() };
      const item = this.itemAt(event.target);
      const origin = this.pending?.origin || this.previous;
      if (item === this.active) {
        this.cancel();
      } else if (this.towardPanel(point, origin)) {
        if (!this.pending) {
          this.pending = { item, origin };
          this.timer = window.setTimeout(() => this.activate(this.pending?.item || null), 300);
        } else {
          this.pending.item = item;
        }
      } else {
        this.activate(item);
      }
      this.previous = point;
    }
  }

  /* ==========================================================================
     Search Modal
     ========================================================================== */

  class SearchModal {
    constructor() {
      this.modal = document.querySelector('[data-search-modal]');
      this.openButtons = document.querySelectorAll('[data-open-search]');
      this.closeButtons = document.querySelectorAll('[data-close-search]');
      this.form = this.modal?.querySelector('[data-search-form]');
      this.input = this.modal?.querySelector('[data-search-input]');
      this.results = this.modal?.querySelector('[data-search-results]');
      this.hint = this.modal?.querySelector('[data-search-hint]');
      this.searchTimer = null;
      this.abortController = null;
      this.latestProducts = [];
      this.latestQuery = '';

      if (!this.modal) return;

      this.init();
    }

    init() {
      this.openButtons.forEach(btn => {
        btn.addEventListener('click', () => this.open());
      });

      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      this.form?.addEventListener('submit', (e) => {
        if (!this.input?.value.trim()) e.preventDefault();
      });

      this.input?.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        const query = this.input.value.trim();
        this.searchTimer = setTimeout(() => this.search(query), 180);
      });

      // If live-event status arrives after predictive search, immediately
      // remove those products from the open results.
      window.addEventListener('at-show-loaded', () => {
        if (this.latestProducts.length > 0) {
          this.renderResults(this.latestProducts, this.latestQuery);
        }
      });

      // Close on backdrop click
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      // Close on Escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    open() {
      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      setTimeout(() => this.input?.focus(), 100);
    }

    close() {
      this.modal.classList.remove('is-open');
      document.body.style.overflow = '';
      this.abortController?.abort();
    }

    async search(query) {
      if (!this.results || !this.hint) return;

      this.abortController?.abort();

      if (query.length < 2) {
        this.results.innerHTML = '';
        this.hint.textContent = 'Start typing to search products.';
        this.hint.hidden = false;
        return;
      }

      this.hint.textContent = 'Searching...';
      this.hint.hidden = false;
      this.results.innerHTML = '';

      this.abortController = new AbortController();
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '8');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,variants.sku,tag,vendor');

      try {
        const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
          signal: this.abortController.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`Search failed: ${response.status}`);

        const data = await response.json();
        const products = await Promise.all(
          (data?.resources?.results?.products || []).map(product => this.enrichUnavailableProduct(product, root))
        );
        this.latestProducts = products;
        this.latestQuery = query;
        this.renderResults(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.results.innerHTML = '';
        this.hint.textContent = 'Search is unavailable right now.';
        this.hint.hidden = false;
      }
    }

    renderResults(products, query) {
      if (!this.results || !this.hint) return;

      const atShowProductIds = this.getAtShowProductIds();
      products = products.filter(product =>
        !atShowProductIds.has(String(product.id)) && this.shouldIncludeProduct(product)
      );

      if (products.length === 0) {
        this.results.innerHTML = '';
        this.hint.textContent = `No products found for "${query}".`;
        this.hint.hidden = false;
        return;
      }

      this.hint.hidden = true;
      this.results.innerHTML = products.map(product => this.renderResult(product)).join('');
    }

    shouldIncludeProduct(product) {
      if (product?.available === true || product?.available === 'true') return true;
      return window.TMMRecentlySold.shouldIncludeInSearch(product);
    }

    getAtShowProductIds() {
      try {
        const cached = JSON.parse(sessionStorage.getItem('pokemon_at_show') || 'null');
        return new Set((cached?.fully_at_show_product_ids || cached?.product_ids || []).map(String));
      } catch (error) {
        return new Set();
      }
    }

    async enrichUnavailableProduct(product, root) {
      if (product?.available === true || product?.available === 'true' || product?.variants?.length || !product?.handle) {
        return product;
      }
      try {
        const response = await fetch(`${root}products/${encodeURIComponent(product.handle)}.js`, {
          signal: this.abortController?.signal,
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return product;
        const details = await response.json();
        return { ...product, variants: details?.variants || [] };
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        return product;
      }
    }

    renderResult(product) {
      const title = this.escapeHtml(product.title || 'Untitled product');
      const url = this.escapeAttribute(product.url || product.handle ? product.url || `/products/${product.handle}` : '#');
      const type = this.escapeHtml(product.type || product.product_type || '');
      const sold = !(product?.available === true || product?.available === 'true')
        ? window.TMMRecentlySold.latestSoldVariant(product, new Date(), true)
        : null;
      const priceValue = product.price_min || product.price;
      const price = sold
        ? this.escapeHtml(`Sold ${sold.display}`)
        : (priceValue ? this.escapeHtml(`$${parseFloat(priceValue).toFixed(2)}`) : '');
      const image = product.image || product.featured_image || {};
      const imageUrl = typeof image === 'string' ? image : image.url || image.src;
      const imageAlt = typeof image === 'object' && image.alt
        ? image.alt
        : `${product.title || 'Product'} product image`;
      const imageHtml = imageUrl
        ? `<img src="${this.escapeAttribute(imageUrl)}" alt="${this.escapeAttribute(imageAlt || product.title || '')}" class="search-result__image" loading="lazy">`
        : '<div class="search-result__placeholder"></div>';

      return `
        <a href="${url}" class="search-result">
          <span class="search-result__media">${imageHtml}</span>
          <span class="search-result__info">
            <span class="search-result__title">${title}</span>
            ${type ? `<span class="search-result__meta">${type}</span>` : ''}
          </span>
          ${price ? `<span class="search-result__price${sold ? ' search-result__price--sold' : ''}">${price}</span>` : ''}
        </a>
      `;
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/"/g, '&quot;');
    }
  }

  /* ==========================================================================
     Cart Drawer (if using drawer style)
     ========================================================================== */

  class CartDrawer {
    constructor() {
      this.drawer = document.querySelector('[data-cart-drawer]');
      this.openButtons = document.querySelectorAll('[data-open-cart]');
      this.closeButtons = document.querySelectorAll('[data-close-cart]');
      this.overlay = document.querySelector('[data-cart-overlay]');
      this.items = this.drawer?.querySelector('[data-cart-items]');
      this.empty = this.drawer?.querySelector('[data-cart-empty]');
      this.footer = this.drawer?.querySelector('[data-cart-footer]');
      this.subtotal = this.drawer?.querySelector('[data-cart-subtotal]');
      this.warning = this.drawer?.querySelector('[data-cart-warning]');
      this.checkoutForm = this.drawer?.querySelector('[data-cart-checkout-form]');
      this.checkoutDisabled = this.drawer?.querySelector('[data-cart-checkout-disabled]');
      this.isLoading = false;

      if (!this.drawer) return;

      this.init();
    }

    init() {
      this.openButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open({ refresh: true });
        });
      });

      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      this.overlay?.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
          this.close();
        }
      });

      window.addEventListener('cart:open', () => {
        this.open({ refresh: true });
      });

      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!this.isProductAddForm(form)) return;
        e.preventDefault();
        e.stopPropagation();
        this.addProduct(form);
      }, true);

      document.addEventListener('click', (e) => {
        const button = e.target.closest('[data-quick-add]');
        if (!button) return;
        e.preventDefault();
        e.stopPropagation();
        this.addVariant(button);
      });

      this.drawer.addEventListener('click', (e) => {
        const removeButton = e.target.closest('[data-cart-remove]');
        if (!removeButton) return;
        e.preventDefault();
        this.removeItem(removeButton.dataset.cartRemove);
      });
    }

    open(options = {}) {
      // inert keeps the closed drawer out of the keyboard tab order.
      if (!this.drawer.classList.contains('is-open')) this.returnFocus = document.activeElement;
      this.drawer.removeAttribute('inert');
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.querySelector('.cart-drawer__close')?.focus({ preventScroll: true });
      this.overlay?.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (options.refresh) this.refresh();
    }

    close() {
      const hadFocus = this.drawer.contains(document.activeElement);
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      this.overlay?.classList.remove('is-open');
      document.body.style.overflow = '';
      if (hadFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    async addProduct(form) {
      if (this.isLoading) return;
      this.setLoading(true);

      try {
        const formData = new FormData(form);
        const variantId = String(formData.get('id') || '');
        if (variantId && await this.cartHasVariant(variantId)) {
          await this.refresh({ force: true });
          this.open();
          this.announce('Already in cart.');
          window.dispatchEvent(new CustomEvent('cart:variant-state', {
            detail: { variantId, inCart: true }
          }));
          return;
        }
        formData.set('quantity', '1');
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add this item to cart.');
        }
        await this.refresh({ force: true });
        this.open();
        this.announce('Added to cart.');
        if (variantId) {
          window.dispatchEvent(new CustomEvent('cart:variant-state', {
            detail: { variantId, inCart: true }
          }));
        }
      } catch (error) {
        this.announce(error.message || 'Unable to add this item to cart.');
      } finally {
        this.setLoading(false);
      }
    }

    async addVariant(button) {
      if (this.isLoading || !(button instanceof HTMLButtonElement)) return;
      const variantId = String(button.dataset.variantId || '');
      if (!variantId) return;

      this.setLoading(true);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      try {
        if (await this.cartHasVariant(variantId)) {
          await this.refresh({ force: true });
          this.open();
          this.announce('Already in cart.');
        } else {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || 'Unable to add this item to cart.');
          }
          await this.refresh({ force: true });
          this.open();
          this.announce('Added to cart.');
        }

        button.classList.add('is-added');
        window.setTimeout(() => button.classList.remove('is-added'), 1200);
        window.dispatchEvent(new CustomEvent('cart:variant-state', {
          detail: { variantId, inCart: true }
        }));
      } catch (error) {
        this.announce(error.message || 'Unable to add this item to cart.');
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        this.setLoading(false);
      }
    }

    isProductAddForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      const action = form.getAttribute('action') || '';
      const isCartAddAction = action.includes('/cart/add');
      const isProductForm = form.querySelector('input[name="form_type"][value="product"]') && form.querySelector('input[name="id"]');
      return Boolean(isCartAddAction || isProductForm);
    }

    async cartHasVariant(variantId) {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return false;
      const cart = await response.json();
      return (cart.items || []).some(item => String(item.variant_id) === String(variantId));
    }

    async removeItem(key) {
      if (!key || this.isLoading) return;
      this.setLoading(true);
      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: key, quantity: 0 })
        });
        if (!response.ok) throw new Error('Unable to remove this item.');
        const cart = await response.json();
        this.render(cart);
        this.announce('Removed from cart.');
      } catch (error) {
        this.announce(error.message || 'Unable to update cart.');
      } finally {
        this.setLoading(false);
      }
    }

    async refresh(options = {}) {
      if (this.isLoading && !options.force) return;
      const shouldOwnLoadingState = !this.isLoading;
      if (shouldOwnLoadingState) this.setLoading(true);
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('Unable to load cart.');
        const cart = await response.json();
        this.render(cart);
      } catch (error) {
        this.announce(error.message || 'Unable to load cart.');
      } finally {
        if (shouldOwnLoadingState) this.setLoading(false);
      }
    }

    render(cart) {
      const items = cart.items || [];
      const hasItems = items.length > 0;
      const hasInStoreOnlyItems = items.some(item => this.isInStoreOnly(item));

      if (this.items) {
        this.items.innerHTML = items.map(item => this.renderItem(item)).join('');
        this.items.hidden = !hasItems;
      }
      if (this.empty) this.empty.hidden = hasItems;
      if (this.footer) this.footer.hidden = !hasItems;
      if (this.subtotal) this.subtotal.textContent = this.formatMoney(cart.total_price || 0);
      if (this.warning) this.warning.hidden = !hasInStoreOnlyItems;
      if (this.checkoutForm) this.checkoutForm.hidden = hasInStoreOnlyItems;
      if (this.checkoutDisabled) this.checkoutDisabled.hidden = !hasInStoreOnlyItems;

      document.querySelectorAll('[data-cart-count]').forEach(badge => {
        badge.textContent = cart.item_count || 0;
      });

      window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    renderItem(item) {
      const image = item.image
        ? `<img src="${this.escapeAttribute(this.imageUrl(item.image, 160))}" alt="${this.escapeAttribute(item.product_title || item.title || '')}" loading="lazy">`
        : '<span class="cart-drawer__image-placeholder"></span>';
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__variant">${this.escapeHtml(item.variant_title)}</p>`
        : '';
      const inStoreOnly = this.isInStoreOnly(item)
        ? '<p class="cart-drawer__item-note">In-store only</p>'
        : '';
      const url = this.escapeAttribute(item.url || '#');
      const key = this.escapeAttribute(item.key || item.id || '');

      return `
        <article class="cart-drawer__item" data-cart-item-key="${key}">
          <a href="${url}" class="cart-drawer__image">${image}</a>
          <div class="cart-drawer__item-info">
            <a href="${url}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title || item.title || 'Untitled product')}</a>
            ${variantTitle}
            ${inStoreOnly}
            <div class="cart-drawer__item-meta">
              <span>Qty ${item.quantity || 1}</span>
              <span>${this.formatMoney(item.final_line_price ?? item.line_price ?? item.price ?? 0)}</span>
            </div>
          </div>
          <button type="button" class="cart-drawer__remove" data-cart-remove="${key}" aria-label="Remove ${this.escapeAttribute(item.product_title || item.title || 'item')}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6 6 18M6 6l12 12"/>
            </svg>
          </button>
        </article>
      `;
    }

    isInStoreOnly(item) {
      const type = String(item.product_type || '').toLowerCase();
      return type.includes('sealed');
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      this.drawer.classList.toggle('is-loading', isLoading);
    }

    formatMoney(cents) {
      const value = Number(cents || 0) / 100;
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(value);
    }

    imageUrl(url, width) {
      if (!url || !url.includes('cdn.shopify.com')) return url || '';
      return url.replace(/(\.[a-zA-Z]+)(\?.*)?$/, `_${width}x$1$2`);
    }

    announce(message) {
      const liveRegion = document.getElementById('aria-live-region');
      if (liveRegion) liveRegion.textContent = message;
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value == null ? '' : String(value);
      return div.innerHTML;
    }

    escapeAttribute(value) {
      return this.escapeHtml(value).replace(/"/g, '&quot;');
    }
  }

  /* ==========================================================================
     Mobile Category Navigation
     ========================================================================== */

  class MobileCategoryNav {
    constructor() {
      this.button = document.querySelector('[data-open-mobile-menu]');
      this.dropdown = document.querySelector('.dropdown-bar');

      if (!this.button || !this.dropdown) return;

      this.init();
    }

    init() {
      this.button.setAttribute('aria-expanded', 'false');
      this.button.setAttribute('aria-controls', 'category-navigation');
      this.dropdown.id = this.dropdown.id || 'category-navigation';

      this.button.addEventListener('click', () => this.toggle());
      this.dropdown.querySelectorAll('[data-open-about], [data-open-store], [data-open-faq]').forEach(button => {
        button.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.close();
        }
      });
    }

    toggle() {
      const isOpen = this.dropdown.classList.toggle('is-mobile-open');
      this.button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      if (!isOpen) {
        document.querySelectorAll('[data-mega-menu-trigger][aria-expanded="true"]').forEach(trigger => {
          trigger.setAttribute('aria-expanded', 'false');
        });
      }
    }

    close() {
      this.dropdown.classList.remove('is-mobile-open');
      this.button.setAttribute('aria-expanded', 'false');
      document.querySelectorAll('[data-mega-menu-trigger][aria-expanded="true"]').forEach(trigger => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }
  }

  /* ==========================================================================
     Account Avatar
     ========================================================================== */

  class AccountAvatar {
    constructor() {
      this.link = document.querySelector('.header-account-link');
      this.image = this.link?.querySelector('[data-header-account-avatar]');
      this.icon = this.link?.querySelector('[data-header-account-icon]');
      this.customerId = this.link?.dataset.customerId;
      this.apiPath = this.link?.dataset.accountApi;
      this.authenticated = this.link?.dataset.accountAuthenticated === 'true';
      this.allowedIds = new Set(['bulbasaur', 'charmander', 'squirtle', 'pikachu', 'eevee', 'chikorita', 'cyndaquil', 'totodile']);

      if (!this.link || !this.image || !this.icon) return;

      window.addEventListener('triple-m:avatar-change', (event) => this.render(event.detail?.id));
      if (this.authenticated) {
        this.render(this.savedId());
        this.loadSyncedAvatar();
      }
    }

    storageKey() {
      return this.customerId ? `tripleMAccountAvatar:${this.customerId}` : null;
    }

    savedId() {
      try {
        const key = this.storageKey();
        if (!key) return null;
        const saved = localStorage.getItem(key);
        if (saved) return saved;

        const legacySaved = localStorage.getItem('tripleMAccountAvatar');
        if (this.allowedIds.has(legacySaved)) {
          localStorage.setItem(key, legacySaved);
          return legacySaved;
        }
        return null;
      } catch {
        return null;
      }
    }

    persist(id) {
      try {
        const key = this.storageKey();
        if (key && this.allowedIds.has(id)) localStorage.setItem(key, id);
      } catch {
        // The header can still render the synced avatar when storage is unavailable.
      }
    }

    async loadSyncedAvatar() {
      if (!this.apiPath) return;
      try {
        const url = new URL(this.apiPath, window.location.origin);
        url.searchParams.set('action', 'preferences');
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) return;
        const payload = await response.json();
        const avatarId = payload.preferences?.avatarId;
        if (!this.allowedIds.has(avatarId)) return;
        this.persist(avatarId);
        this.render(avatarId);
      } catch {
        // Device storage remains the fallback when account syncing is unavailable.
      }
    }

    render(id) {
      if (!this.allowedIds.has(id)) return;
      const src = this.image.getAttribute(`data-avatar-src-${id}`);
      if (!src) return;

      this.image.src = src;
      this.image.hidden = false;
      this.icon.hidden = true;
      this.link.classList.add('has-account-avatar');
      this.link.setAttribute('aria-label', `Account, ${id} avatar`);
    }
  }

  /* ==========================================================================
     Theme Toggle (Dark Mode)
     ========================================================================== */

  class ThemeToggle {
    constructor() {
      this.btn = document.querySelector('[data-theme-toggle]');
      this.moonIcon = this.btn?.querySelector('.theme-toggle__moon');
      this.sunIcon = this.btn?.querySelector('.theme-toggle__sun');

      if (!this.btn) return;

      this.isDark = document.body.classList.contains('dark-mode');
      this.updateIcon();
      this.btn.addEventListener('click', () => this.toggle());
    }

    toggle() {
      this.isDark = !this.isDark;
      document.body.classList.toggle('dark-mode', this.isDark);
      localStorage.setItem('darkMode', this.isDark);
      this.updateIcon();
    }

    updateIcon() {
      if (this.moonIcon && this.sunIcon) {
        this.moonIcon.style.display = this.isDark ? 'none' : '';
        this.sunIcon.style.display = this.isDark ? '' : 'none';
      }
    }
  }

  /* ==========================================================================
     Smart Header (hide on scroll down, show on scroll up)
     ========================================================================== */

  class SmartHeader {
    constructor() {
      this.lastScrollY = window.scrollY;
      this.threshold = 10; // Minimum scroll distance before toggling
      this.headerHeight = 156; // approx total header stack height

      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    }

    onScroll() {
      const currentY = window.scrollY;
      const diff = currentY - this.lastScrollY;

      // If near top of page, always show header
      if (currentY < this.headerHeight) {
        document.body.classList.remove('header-hidden');
        this.lastScrollY = currentY;
        return;
      }

      // Scrolling down past threshold → hide
      if (diff > this.threshold) {
        document.body.classList.add('header-hidden');
        this.lastScrollY = currentY;
      }
      // Scrolling up past threshold → show
      else if (diff < -this.threshold) {
        document.body.classList.remove('header-hidden');
        this.lastScrollY = currentY;
      }
    }
  }

  /* ==========================================================================
     Initialize
     ========================================================================== */

  function init() {
    // Initialize mega menus
    document.querySelectorAll('[data-mega-menu]').forEach(menu => {
      new MegaMenu(menu);
    });
    document.querySelectorAll('.dropdown-bar__inner').forEach(root => new MenuHoverIntent(root, true));
    document.querySelectorAll('.dropdown-bar .mega-menu__list').forEach(root => new MenuHoverIntent(root));

    // Initialize search modal
    new SearchModal();

    // Initialize cart drawer
    const cartDrawer = new CartDrawer();
    const params = new URLSearchParams(window.location.search);
    if (cartDrawer.drawer && params.get('cart') === 'open') {
      cartDrawer.open({ refresh: true });
      params.delete('cart');
      const nextUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
      window.history.replaceState({}, '', nextUrl);
    }

    // Initialize mobile category navigation
    new MobileCategoryNav();

    // Initialize theme toggle
    new ThemeToggle();

    // Initialize saved account avatar
    new AccountAvatar();

    // Initialize smart header (hide on scroll down, show on scroll up)
    new SmartHeader();

  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
